<!-- src/lib/components/SettingsSubapp.svelte -->
<!--
  Full-page settings sub-application. Replaces the crammed sidebar
  settings branch. Takes over the entire app area (NavRail hidden in
  settings mode) and provides its own left-nav across categories:

    General / Appearance / Integrations / Data / Shortcuts / Workspace

  Round 5 of the 50-refinements plan (see
  docs/superpowers/specs/2026-04-11-50-refinements.md items 5, 29, 33).
  Further categories (Profile / Notifications / Labels / Templates /
  Teams / Members / API / Applications / Billing) are stubs for now,
  added as placeholder pages so the nav IA is already in place.
-->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api, apiFetch } from '../api.ts';
  import McpRegisterPanel from './McpRegisterPanel.svelte';
  import WorkspaceMembersSettings from './WorkspaceMembersSettings.svelte';

  type CategoryId =
    | 'general' | 'appearance' | 'profile' | 'notifications'
    | 'integrations' | 'data' | 'shortcuts'
    | 'labels' | 'templates' | 'statuses'
    | 'agents'
    | 'workspace' | 'teams' | 'members' | 'api' | 'applications';

  interface Props {
    onClose:        () => void;
    initialCategory?: CategoryId;
    theme?:         'dark' | 'light';
    onToggleTheme?: () => void;
    density?:       'compact' | 'comfortable' | 'spacious';
    onSetDensity?:  (d: 'compact' | 'comfortable' | 'spacious') => void;
    onOpenImport?:  () => void;
  }
  let {
    onClose,
    initialCategory,
    theme = 'dark',
    onToggleTheme,
    density = 'comfortable',
    onSetDensity,
    onOpenImport,
  }: Props = $props();

  interface Category { id: CategoryId; label: string; group: string; stub?: boolean; }
  const categories: Category[] = [
    { id: 'general',       label: 'General',           group: 'Account' },
    { id: 'appearance',    label: 'Appearance',        group: 'Account' },
    { id: 'profile',       label: 'Profile',           group: 'Account',   stub: true },
    { id: 'notifications', label: 'Notifications',     group: 'Account',   stub: true },
    { id: 'integrations',  label: 'Integrations',      group: 'Account' },
    { id: 'shortcuts',     label: 'Keyboard',          group: 'Account' },

    { id: 'labels',        label: 'Issue labels',      group: 'Project' },
    { id: 'templates',     label: 'Templates',         group: 'Project' },
    { id: 'statuses',      label: 'Statuses',          group: 'Project',   stub: true },

    { id: 'agents' as CategoryId,  label: 'Agent personalization', group: 'Features' },

    { id: 'workspace',     label: 'Workspace',         group: 'Workspace' },
    { id: 'data',          label: 'Import & export',   group: 'Workspace' },
    { id: 'teams',         label: 'Teams',             group: 'Workspace', stub: true },
    { id: 'members',       label: 'Members',           group: 'Workspace' },
    { id: 'api',           label: 'API',               group: 'Workspace', stub: true },
    { id: 'applications',  label: 'Applications',      group: 'Workspace', stub: true },
  ];
  const groupOrder = ['Account', 'Project', 'Features', 'Workspace'];

  let activeCategory = $state<CategoryId>('general');

  $effect(() => {
    if (initialCategory && activeCategory !== initialCategory) {
      activeCategory = initialCategory;
    }
  });

  // ── General (About) — read version from /health ────────────────
  interface Health {
    version:         string;
    git_sha?:        string;
    status:          string;
    uptime_seconds:  number;
    queue_depth?:    number;
    running_agents?: number;
    timestamp?:      string;
  }
  let health = $state<Health | null>(null);
  let healthError = $state<string | null>(null);

  async function loadHealth() {
    healthError = null;
    try {
      health = await api<Health>('/health');
    } catch (e: unknown) {
      healthError = e instanceof Error ? e.message : 'Failed to read /health';
    }
  }

  // Load once on mount + whenever General is selected.
  $effect(() => {
    if (activeCategory === 'general') void loadHealth();
  });

  // ── Integrations — Connected Accounts (Task 11) ───────────────
  type OauthProvider = 'linear' | 'plane' | 'asana' | 'figma';
  interface OauthStatus {
    provider:     string;
    connected:    boolean;
    account_name: string | null;
  }
  interface ProviderMeta { id: OauthProvider; label: string; badge: string; }
  const PROVIDERS: ProviderMeta[] = [
    { id: 'linear', label: 'Linear', badge: 'L' },
    { id: 'plane',  label: 'Plane',  badge: 'P' },
    { id: 'asana',  label: 'Asana',  badge: 'A' },
    { id: 'figma',  label: 'Figma',  badge: 'F' },
  ];

  let oauthStatuses   = $state<Record<OauthProvider, OauthStatus | null>>({ linear: null, plane: null, asana: null, figma: null });
  let oauthLoading    = $state(false);
  let oauthError      = $state<string | null>(null);
  // Per-provider inline connect form state
  let connectingFor   = $state<OauthProvider | null>(null);
  let connectToken    = $state('');
  let connectName     = $state('');
  let connectBusy     = $state(false);
  let connectError    = $state<string | null>(null);

  async function loadOauthStatuses() {
    oauthLoading = true;
    oauthError   = null;
    try {
      const results = await Promise.allSettled(
        PROVIDERS.map(async (p) => {
          const status = await api<OauthStatus>(`/api/oauth/${p.id}/status`);
          return { provider: p.id, status };
        })
      );
      const next = { ...oauthStatuses };
      for (const r of results) {
        if (r.status === 'fulfilled') {
          next[r.value.provider as OauthProvider] = r.value.status;
        }
      }
      oauthStatuses = next;
    } catch (e: unknown) {
      oauthError = 'Could not load connected accounts.';
    } finally {
      oauthLoading = false;
    }
  }

  async function connectProvider(provider: OauthProvider) {
    connectBusy  = true;
    connectError = null;
    try {
      await api('/api/oauth/token', {
        method: 'POST',
        body: JSON.stringify({ provider, token: connectToken, account_name: connectName || null }),
      });
      connectingFor = null;
      connectToken  = '';
      connectName   = '';
      await loadOauthStatuses();
    } catch (e: unknown) {
      connectError = e instanceof Error ? e.message : 'Connection failed.';
    } finally {
      connectBusy = false;
    }
  }

  async function disconnectProvider(provider: OauthProvider) {
    try {
      await api(`/api/oauth/${provider}`, { method: 'DELETE' });
      await loadOauthStatuses();
    } catch {}
  }

  $effect(() => {
    if (activeCategory === 'integrations') void loadOauthStatuses();
  });

  // ── Agent personalization (Round 16b, refinement #30) ─────────
  const AGENT_GUIDANCE_KEY = 'agentboard.agentGuidance';
  let agentGuidance = $state(
    (() => { try { return localStorage.getItem(AGENT_GUIDANCE_KEY) ?? ''; } catch { return ''; } })()
  );
  function saveGuidance() {
    try { localStorage.setItem(AGENT_GUIDANCE_KEY, agentGuidance); } catch {}
  }

  // ── Labels manager (Round 16, refinement #31) ─────────────────
  import LabelChip from './LabelChip.svelte';
  import type { Label } from '../types.ts';

  let projectLabels = $state<Label[]>([]);
  let labelsLoading = $state(false);
  let newLabelName  = $state('');
  let newLabelColor = $state('oklch(0.68 0.18 265)');

  async function loadLabels() {
    if (!pmStore.activeProjectId) return;
    labelsLoading = true;
    try {
      projectLabels = await api<Label[]>(`/api/v1/projects/${pmStore.activeProjectId}/labels`);
    } catch { projectLabels = []; }
    finally { labelsLoading = false; }
  }

  async function createLabel() {
    if (!pmStore.activeProjectId || !newLabelName.trim()) return;
    try {
      const label = await api<Label>(`/api/v1/projects/${pmStore.activeProjectId}/labels`, {
        method: 'POST',
        body: JSON.stringify({ name: newLabelName.trim(), color: newLabelColor }),
      });
      projectLabels = [...projectLabels, label];
      newLabelName = '';
    } catch {}
  }

  $effect(() => { if (activeCategory === 'labels') void loadLabels(); });

  // ── Templates (Round 21, refinement #32) ─────────────────────
  interface Template { id: string; workspace_id: string; kind: string; name: string; spec: string; usage_count: number; }
  let templates = $state<Template[]>([]);
  let newTplName = $state('');
  let newTplKind = $state('issue');
  let newTplSpec = $state('{}');

  async function loadTemplates() {
    if (!pmStore.activeWorkspaceId) return;
    try {
      templates = await api<Template[]>(`/api/v1/workspaces/${pmStore.activeWorkspaceId}/templates`);
    } catch { templates = []; }
  }
  async function createTemplate() {
    if (!pmStore.activeWorkspaceId || !newTplName.trim()) return;
    try {
      const t = await api<Template>(`/api/v1/workspaces/${pmStore.activeWorkspaceId}/templates`, {
        method: 'POST',
        body: JSON.stringify({ kind: newTplKind, name: newTplName.trim(), spec: newTplSpec }),
      });
      templates = [...templates, t];
      newTplName = '';
      newTplSpec = '{}';
    } catch {}
  }
  async function deleteTemplate(id: string) {
    await api(`/api/v1/templates/${id}`, { method: 'DELETE' }).catch(() => {});
    templates = templates.filter(t => t.id !== id);
  }
  $effect(() => { if (activeCategory === 'templates') void loadTemplates(); });

  // ── Workspace danger zone ──────────────────────────────────────
  let deleteInput    = $state('');
  let deleteBusy     = $state(false);
  let deleteError    = $state<string | null>(null);

  const activeWs = $derived(
    pmStore.workspaces.find((w) => w.id === pmStore.activeWorkspaceId) ?? null
  );
  const canDelete = $derived(
    !!activeWs && deleteInput.trim() === activeWs.slug
  );

  async function confirmDelete() {
    if (!canDelete || !activeWs) return;
    const slug = activeWs.slug;
    if (!window.confirm(`Last chance — permanently delete workspace "${slug}" and everything in it?`)) return;
    deleteBusy = true;
    deleteError = null;
    try {
      const res = await apiFetch(`/api/v1/workspaces/${activeWs.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      // Remove from local store, pick a different active workspace.
      pmStore.workspaces = pmStore.workspaces.filter((w) => w.id !== activeWs.id);
      const next = pmStore.workspaces[0];
      pmStore.activeWorkspaceId = next ? next.id : null;
      pmStore.activeProjectId   = null;
      deleteInput = '';
      onClose(); // back to Tasks
    } catch (e: unknown) {
      deleteError = e instanceof Error ? e.message : 'Delete failed';
    } finally {
      deleteBusy = false;
    }
  }

</script>

<div class="settings-subapp" role="region" aria-label="Settings">
  <!-- Left category nav -->
  <aside class="settings-nav">
    <button class="back-btn" onclick={onClose} aria-label="Back to app">
      <span class="back-arrow">←</span>
      <span>Back to app</span>
    </button>

    {#each groupOrder as group}
      <div class="category-group">
        <span class="group-label">{group}</span>
        <ul class="category-list">
          {#each categories.filter((c) => c.group === group) as cat (cat.id)}
            <li>
              <button
                class="category-btn"
                class:active={activeCategory === cat.id}
                class:is-stub={cat.stub}
                onclick={() => { activeCategory = cat.id; }}
                title={cat.stub ? `${cat.label} — coming soon` : cat.label}
              >
                {#if cat.stub}
                  <span class="stub-lock" aria-hidden="true">🔒</span>
                {/if}
                <span class="cat-label-text">{cat.label}</span>
              </button>
            </li>
          {/each}
        </ul>
      </div>
    {/each}
  </aside>

  <!-- Right content column -->
  <main class="settings-content">
    {#if activeCategory === 'general'}
      <header class="page-header">
        <h1>General</h1>
        <p class="page-sub">About this AgentBoard instance.</p>
      </header>

      <section class="card">
        <h2>About</h2>
        {#if healthError}
          <p class="error">{healthError}</p>
        {:else if health}
          <dl class="kv-list">
            <dt>Version</dt>
            <dd>v{health.version}</dd>
            {#if health.git_sha && health.git_sha !== 'dev'}
              <dt>Build</dt>
              <dd><code>{health.git_sha.slice(0, 8)}</code></dd>
            {/if}
            <dt>Status</dt>
            <dd class="status-ok">● {health.status}</dd>
            <dt>Uptime</dt>
            <dd>{Math.round(health.uptime_seconds / 60)} min</dd>
            {#if typeof health.queue_depth === 'number'}
              <dt>Queue depth</dt>
              <dd>{health.queue_depth}</dd>
            {/if}
            {#if typeof health.running_agents === 'number'}
              <dt>Running agents</dt>
              <dd>{health.running_agents}</dd>
            {/if}
          </dl>
          <p class="card-footer-note">
            Source: <a href="https://github.com/cashcon57/agentboard" target="_blank" rel="noopener noreferrer">github.com/cashcon57/agentboard</a>
          </p>
        {:else}
          <p class="muted">Reading <code>/health</code>…</p>
        {/if}
      </section>

    {:else if activeCategory === 'appearance'}
      <header class="page-header">
        <h1>Appearance</h1>
        <p class="page-sub">Theme and density preferences, applied across the whole app.</p>
      </header>

      <section class="card">
        <h2>Theme</h2>
        <div class="settings-row">
          <span>Current theme</span>
          <button class="btn secondary" onclick={() => onToggleTheme?.()}>
            {theme === 'dark' ? 'Dark' : 'Light'} · click to toggle
          </button>
        </div>
      </section>

      <section class="card">
        <h2>Density</h2>
        <div class="density-row">
          {#each (['compact', 'comfortable', 'spacious'] as const) as d}
            <button
              class="density-pill"
              class:active={density === d}
              onclick={() => onSetDensity?.(d)}
            >{d}</button>
          {/each}
        </div>
      </section>

    {:else if activeCategory === 'integrations'}
      <header class="page-header">
        <h1>Integrations</h1>
        <p class="page-sub">Hook external tools into AgentBoard.</p>
      </header>

      <section class="card">
        <h2>Claude Code MCP</h2>
        <McpRegisterPanel />
      </section>

      <section class="card">
        <h2>Connected Accounts</h2>
        {#if oauthError}
          <p class="error">{oauthError}</p>
        {:else if oauthLoading && Object.values(oauthStatuses).every(s => s === null)}
          <p class="muted">Loading…</p>
        {:else}
          <div class="oauth-list">
            {#each PROVIDERS as p (p.id)}
              {@const status = oauthStatuses[p.id]}
              <div class="oauth-row">
                <span class="oauth-badge">{p.badge}</span>
                <div class="oauth-info">
                  <span class="oauth-label">{p.label}</span>
                  {#if status?.connected}
                    <span class="oauth-account">{status.account_name ?? 'Connected'}</span>
                  {:else}
                    <span class="oauth-account muted">Not connected</span>
                  {/if}
                </div>
                <div class="oauth-actions">
                  {#if status?.connected}
                    <button
                      class="btn secondary"
                      onclick={() => { void disconnectProvider(p.id); }}
                    >Disconnect</button>
                  {:else if connectingFor === p.id}
                    <div class="oauth-connect-form">
                      <input
                        class="oauth-token-input"
                        type="password"
                        placeholder="API token"
                        bind:value={connectToken}
                        disabled={connectBusy}
                      />
                      <input
                        class="oauth-name-input"
                        type="text"
                        placeholder="Account name (optional)"
                        bind:value={connectName}
                        disabled={connectBusy}
                      />
                      <button
                        class="btn primary"
                        onclick={() => { void connectProvider(p.id); }}
                        disabled={connectBusy || !connectToken.trim()}
                      >{connectBusy ? 'Saving…' : 'Save'}</button>
                      <button
                        class="btn ghost"
                        onclick={() => { connectingFor = null; connectToken = ''; connectName = ''; connectError = null; }}
                        disabled={connectBusy}
                      >Cancel</button>
                    </div>
                    {#if connectError}
                      <p class="error">{connectError}</p>
                    {/if}
                  {:else}
                    <button
                      class="btn secondary"
                      onclick={() => { connectingFor = p.id; connectToken = ''; connectName = ''; connectError = null; }}
                    >Connect</button>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </section>

      <section class="card">
        <h2>Migration History</h2>
        <p class="muted">No migrations yet.</p>
        <button class="btn primary" onclick={() => onOpenImport?.()}>Start Migration</button>
      </section>

    {:else if activeCategory === 'data'}
      <header class="page-header">
        <h1>Import & export</h1>
        <p class="page-sub">Move data in and out of AgentBoard.</p>
      </header>

      <section class="card">
        <h2>Import from Plane / Linear</h2>
        <p class="muted">Paste a JSON export from Plane or Linear to hydrate projects and cards.</p>
        <button class="btn primary" onclick={() => onOpenImport?.()}>Open importer</button>
      </section>

    {:else if activeCategory === 'shortcuts'}
      <header class="page-header">
        <h1>Keyboard</h1>
        <p class="page-sub">Default shortcuts — configurable in a future release.</p>
      </header>

      <section class="card">
        <h2>Navigation</h2>
        <div class="shortcut-list">
          <div class="shortcut"><kbd>⌘K</kbd><span>Command palette</span></div>
          <div class="shortcut"><kbd>⌘B</kbd><span>Toggle file explorer</span></div>
          <div class="shortcut"><kbd>Ctrl `</kbd><span>Toggle terminal</span></div>
        </div>
      </section>
      <section class="card">
        <h2>Cards</h2>
        <div class="shortcut-list">
          <div class="shortcut"><kbd>/</kbd><span>Search board</span></div>
          <div class="shortcut"><kbd>F</kbd><span>Focus mode (card open)</span></div>
          <div class="shortcut"><kbd>Esc</kbd><span>Close card drawer</span></div>
        </div>
      </section>
      <section class="card">
        <h2>Studio</h2>
        <div class="shortcut-list">
          <div class="shortcut"><kbd>V R O A T</kbd><span>Annotate tools</span></div>
        </div>
      </section>

    {:else if activeCategory === 'templates'}
      <header class="page-header">
        <h1>Templates</h1>
        <p class="page-sub">Prefill cards or projects from reusable specs.</p>
      </header>

      <section class="card">
        <h2>Create template</h2>
        <div class="label-create-row">
          <select bind:value={newTplKind}>
            <option value="issue">Issue</option>
            <option value="project">Project</option>
          </select>
          <input class="label-name-input" type="text" placeholder="Template name" bind:value={newTplName} />
          <button class="btn primary" onclick={createTemplate} disabled={!newTplName.trim()}>Add</button>
        </div>
        <div class="label-create-row" style="margin-top: 0.5rem;">
          <textarea class="guidance-textarea" bind:value={newTplSpec} rows={4} placeholder="JSON spec, e.g. title, priority, description"></textarea>
        </div>
      </section>

      <section class="card">
        <h2>Templates</h2>
        {#if templates.length === 0}
          <p class="muted">No templates yet.</p>
        {:else}
          <table class="labels-table">
            <thead><tr><th>Kind</th><th>Name</th><th>Uses</th><th></th></tr></thead>
            <tbody>
              {#each templates as t (t.id)}
                <tr>
                  <td><code>{t.kind}</code></td>
                  <td>{t.name}</td>
                  <td>{t.usage_count}</td>
                  <td><button class="btn ghost" onclick={() => { deleteTemplate(t.id); }}>×</button></td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </section>

    {:else if activeCategory === 'agents'}
      <header class="page-header">
        <h1>Agent personalization</h1>
        <p class="page-sub">Persistent context injected into every agent spawn from this workspace.</p>
      </header>

      <section class="card">
        <h2>Guidance</h2>
        <p class="muted">Instructions, constraints, or domain knowledge the agent should always consider. Saved locally.</p>
        <textarea
          class="guidance-textarea"
          bind:value={agentGuidance}
          onblur={saveGuidance}
          placeholder="e.g. Always run tests before committing. Use conventional commits. Prefer Rust over Python for new services."
          rows={8}
        ></textarea>
      </section>

      <section class="card">
        <h2>Personas</h2>
        <p class="muted">Create agent personas in the Agents → Personas tab. Each persona can have its own system prompt, preferred tier, and rules. When you assign a persona to a card, its context is injected automatically.</p>
      </section>

    {:else if activeCategory === 'labels'}
      <header class="page-header">
        <h1>Issue labels</h1>
        <p class="page-sub">Labels for the active project. Click a chip to preview.</p>
      </header>

      <section class="card">
        <h2>Create label</h2>
        <div class="label-create-row">
          <input class="label-name-input" type="text" placeholder="Label name" bind:value={newLabelName} />
          <input class="label-color-input" type="text" placeholder="oklch(...)" bind:value={newLabelColor} />
          <button class="btn primary" onclick={createLabel} disabled={!newLabelName.trim()}>Add</button>
        </div>
      </section>

      <section class="card">
        <h2>Labels</h2>
        {#if labelsLoading}
          <p class="muted">Loading…</p>
        {:else if projectLabels.length === 0}
          <p class="muted">No labels yet. Create one above.</p>
        {:else}
          <table class="labels-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Name</th>
                <th>Color</th>
              </tr>
            </thead>
            <tbody>
              {#each projectLabels as label (label.id)}
                <tr>
                  <td><LabelChip name={label.name} color={label.color} /></td>
                  <td>{label.name}</td>
                  <td><code>{label.color}</code></td>
                </tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </section>

    {:else if activeCategory === 'workspace'}
      <header class="page-header">
        <h1>Workspace</h1>
        <p class="page-sub">Current workspace settings.</p>
      </header>

      {#if activeWs}
        <section class="card">
          <h2>Details</h2>
          <dl class="kv-list">
            <dt>Name</dt>
            <dd>{activeWs.name}</dd>
            <dt>Slug</dt>
            <dd><code>{activeWs.slug}</code></dd>
            <dt>Created</dt>
            <dd>{new Date(activeWs.created_at).toLocaleString()}</dd>
          </dl>
        </section>

        <section class="card danger">
          <h2>Danger zone</h2>
          <p class="danger-intro">
            Permanently delete this workspace and <strong>every</strong> project,
            board, card, label, channel, and message inside it. This cannot be
            undone.
          </p>
          <p class="danger-hint">
            To confirm, type <code>{activeWs.slug}</code> below.
          </p>
          <div class="danger-row">
            <input
              type="text"
              class="danger-input"
              placeholder={activeWs.slug}
              bind:value={deleteInput}
              disabled={deleteBusy}
              autocomplete="off"
            />
            <button
              class="btn danger"
              onclick={confirmDelete}
              disabled={!canDelete || deleteBusy}
            >
              {deleteBusy ? 'Deleting…' : 'Delete workspace'}
            </button>
          </div>
          {#if deleteError}
            <p class="error">{deleteError}</p>
          {/if}
        </section>
      {:else}
        <p class="muted">No active workspace selected.</p>
      {/if}

    {:else if activeCategory === 'members'}
      <header class="page-header">
        <h1>Members</h1>
        <p class="page-sub">Manage who has access to this workspace.</p>
      </header>
      <WorkspaceMembersSettings />

    {:else}
      <div class="settings-stub">
        <span class="stub-chip">Soon</span>
        <h2>Coming in a future release</h2>
        <p>This section is being built. Check back soon.</p>
      </div>
    {/if}
  </main>
</div>

<style>
  .settings-subapp {
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    background: oklch(0.065 0.015 275);
    color: oklch(0.80 0.05 310 / 0.85);
  }

  /* ── Left nav ──────────────────────────────────────── */
  .settings-nav {
    width: 240px;
    flex-shrink: 0;
    background: oklch(0.095 0.018 275);
    border-right: 1px solid oklch(0.30 0.05 275 / 0.20);
    padding: 1rem 0.75rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    background: transparent;
    border: none;
    padding: 0.4rem 0.55rem;
    color: oklch(0.72 0.06 310 / 0.80);
    font-size: 0.82rem;
    cursor: pointer;
    border-radius: 4px;
    transition: background 100ms ease, color 100ms ease;
    text-align: left;
  }
  .back-btn:hover {
    background: oklch(0.17 0.025 275);
    color: oklch(0.94 0.08 310);
  }
  .back-arrow {
    font-size: 1rem;
    line-height: 1;
  }

  .category-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .group-label {
    font-size: 0.62rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.55 0.04 310 / 0.55);
    padding: 0 0.55rem;
  }
  .category-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
  }
  .category-btn {
    width: 100%;
    background: transparent;
    border: none;
    color: oklch(0.80 0.05 310 / 0.75);
    font-size: 0.78rem;
    padding: 0.35rem 0.55rem;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.4rem;
    transition: background 100ms ease, color 100ms ease;
  }
  .category-btn:hover {
    background: oklch(0.17 0.025 275);
    color: oklch(0.94 0.08 310);
  }
  .category-btn.active {
    background: oklch(0.22 0.08 310 / 0.35);
    color: oklch(0.96 0.08 310);
  }
  .category-btn.is-stub {
    opacity: 0.55;
  }
  .category-btn.is-stub:hover {
    opacity: 0.75;
  }
  .stub-lock {
    font-size: 0.62rem;
    filter: grayscale(0.3);
    flex-shrink: 0;
  }
  .cat-label-text {
    flex: 1;
  }

  /* ── Right content ─────────────────────────────────── */
  .settings-content {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    padding: 2.5rem 3rem;
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
    max-width: 820px;
  }
  .page-header h1 {
    margin: 0 0 0.25rem 0;
    font-size: 1.6rem;
    font-weight: 600;
    color: oklch(0.94 0.08 310);
    letter-spacing: -0.01em;
  }
  .page-sub {
    margin: 0;
    color: oklch(0.60 0.04 310 / 0.65);
    font-size: 0.85rem;
  }

  /* ── Card (section) ────────────────────────────────── */
  .card {
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 6px;
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .card h2 {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: oklch(0.72 0.08 310 / 0.75);
  }
  /* ── Stub page (#31) ───────────────────────────────────── */
  .settings-stub {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; height: 100%; gap: 1rem;
    color: oklch(0.55 0.04 310 / 0.6);
  }
  .stub-chip {
    background: oklch(0.72 0.20 80 / 0.15);
    border: 1px solid oklch(0.72 0.20 80 / 0.35);
    color: oklch(0.82 0.18 80);
    padding: 0.2rem 0.6rem; border-radius: 4px;
    font-size: 0.70rem; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.10em;
  }
  .card-footer-note {
    margin: 0;
    font-size: 0.72rem;
    color: oklch(0.55 0.04 310 / 0.55);
  }
  .card-footer-note a {
    color: oklch(0.68 0.12 265);
    text-decoration: none;
  }
  .card-footer-note a:hover { text-decoration: underline; }

  /* ── Key-value list (About, Workspace details) ─────── */
  .kv-list {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 0.45rem 1rem;
    margin: 0;
    font-size: 0.82rem;
  }
  .kv-list dt {
    color: oklch(0.60 0.04 310 / 0.65);
    font-weight: 500;
  }
  .kv-list dd {
    margin: 0;
    color: oklch(0.92 0.06 310);
  }
  .status-ok { color: oklch(0.78 0.16 150); }

  /* ── Settings rows ─────────────────────────────────── */
  .settings-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.82rem;
  }
  .density-row {
    display: flex;
    gap: 0.35rem;
  }
  .density-pill {
    padding: 0.35rem 0.8rem;
    background: oklch(0.17 0.025 275);
    color: oklch(0.72 0.05 310 / 0.75);
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    border-radius: 3px;
    font-size: 0.72rem;
    text-transform: capitalize;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
  }
  .density-pill:hover {
    background: oklch(0.22 0.03 275);
    color: oklch(0.92 0.06 310);
  }
  .density-pill.active {
    background: oklch(0.30 0.18 310 / 0.45);
    color: oklch(0.96 0.06 310);
    border-color: oklch(0.72 0.26 310 / 0.50);
  }

  /* ── Shortcut list ─────────────────────────────────── */
  .shortcut-list {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }
  .shortcut {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.78rem;
    color: oklch(0.80 0.05 310 / 0.80);
  }
  .shortcut kbd {
    display: inline-block;
    min-width: 3.5rem;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
    background: oklch(0.17 0.025 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
    color: oklch(0.88 0.06 310);
  }

  /* ── Buttons ───────────────────────────────────────── */
  .btn {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.45rem 0.85rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn.primary {
    background: oklch(0.30 0.18 310 / 0.45);
    color: oklch(0.96 0.06 310);
    border: 1px solid oklch(0.72 0.26 310 / 0.50);
  }
  .btn.primary:hover:not(:disabled) {
    background: oklch(0.38 0.22 310 / 0.58);
  }
  .btn.secondary {
    background: oklch(0.17 0.025 275);
    color: oklch(0.82 0.06 310 / 0.85);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
  }
  .btn.secondary:hover:not(:disabled) {
    background: oklch(0.22 0.03 275);
    border-color: oklch(0.40 0.08 310 / 0.50);
  }
  .btn.danger {
    background: oklch(0.30 0.18 25 / 0.30);
    color: oklch(0.92 0.14 25);
    border: 1px solid oklch(0.62 0.26 25 / 0.50);
  }
  .btn.danger:hover:not(:disabled) {
    background: oklch(0.40 0.22 25 / 0.45);
    border-color: oklch(0.68 0.28 25 / 0.70);
  }

  /* ── Danger zone styling ───────────────────────────── */
  .card.danger {
    border-color: oklch(0.62 0.22 25 / 0.35);
    background: oklch(0.12 0.03 25 / 0.15);
  }
  .card.danger h2 {
    color: oklch(0.82 0.18 25 / 0.80);
  }
  .danger-intro {
    margin: 0;
    font-size: 0.82rem;
    color: oklch(0.82 0.05 310 / 0.80);
    line-height: 1.55;
  }
  .danger-intro strong {
    color: oklch(0.92 0.14 25);
  }
  .danger-hint {
    margin: 0;
    font-size: 0.74rem;
    color: oklch(0.65 0.04 310 / 0.70);
  }
  .danger-hint code {
    color: oklch(0.92 0.14 25);
    background: oklch(0.30 0.14 25 / 0.20);
    padding: 0.05rem 0.35rem;
    border-radius: 2px;
  }
  .danger-row {
    display: flex;
    gap: 0.6rem;
    align-items: center;
  }
  .danger-input {
    flex: 1;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    padding: 0.45rem 0.65rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
    color: oklch(0.94 0.08 310);
    outline: none;
  }
  .danger-input:focus {
    border-color: oklch(0.62 0.26 25 / 0.55);
  }

  /* ── Shared helpers ────────────────────────────────── */
  .muted {
    margin: 0;
    font-size: 0.78rem;
    color: oklch(0.60 0.04 310 / 0.60);
  }
  .error {
    margin: 0.25rem 0 0 0;
    font-size: 0.74rem;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.72 0.22 25 / 0.90);
  }
  code {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.92em;
    padding: 0.05rem 0.3rem;
    background: oklch(0.22 0.03 275 / 0.45);
    border-radius: 2px;
  }

  /* ── Agent personalization (#30) ─────────────────── */
  .guidance-textarea {
    width: 100%;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    padding: 0.65rem 0.75rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 4px;
    color: oklch(0.92 0.06 310);
    outline: none;
    resize: vertical;
    line-height: 1.55;
    box-sizing: border-box;
  }
  .guidance-textarea:focus {
    border-color: oklch(0.72 0.26 310 / 0.55);
  }

  /* ── Labels manager (#31) ──────────────────────── */
  .label-create-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .label-name-input, .label-color-input {
    font-size: 0.78rem;
    padding: 0.4rem 0.6rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
    color: oklch(0.92 0.06 310);
    outline: none;
  }
  .label-name-input { flex: 1; }
  .label-color-input { width: 160px; font-family: 'JetBrains Mono', monospace; font-size: 0.72rem; }
  .labels-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.78rem;
  }
  .labels-table th {
    text-align: left;
    padding: 0.4rem 0.6rem;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: oklch(0.55 0.04 310 / 0.55);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
  }
  .labels-table td {
    padding: 0.45rem 0.6rem;
    color: oklch(0.88 0.05 310);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.10);
  }

  /* ── Connected Accounts (Task 11) ─────────────────── */
  .oauth-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }
  .oauth-row {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 0.65rem 0.75rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 4px;
    font-size: 0.82rem;
  }
  .oauth-badge {
    flex-shrink: 0;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: oklch(0.30 0.18 310 / 0.30);
    border: 1px solid oklch(0.72 0.26 310 / 0.35);
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 700;
    color: oklch(0.90 0.14 310);
  }
  .oauth-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .oauth-label {
    font-weight: 600;
    color: oklch(0.92 0.06 310);
  }
  .oauth-account {
    font-size: 0.74rem;
    color: oklch(0.72 0.12 150);
  }
  .oauth-account.muted {
    color: oklch(0.55 0.04 310 / 0.60);
    font-size: 0.74rem;
    margin: 0;
  }
  .oauth-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.4rem;
  }
  .oauth-connect-form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
    justify-content: flex-end;
  }
  .oauth-token-input,
  .oauth-name-input {
    font-size: 0.75rem;
    padding: 0.38rem 0.55rem;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.40);
    border-radius: 3px;
    color: oklch(0.92 0.06 310);
    outline: none;
  }
  .oauth-token-input { width: 180px; font-family: 'JetBrains Mono', monospace; }
  .oauth-name-input  { width: 150px; }
  .oauth-token-input:focus,
  .oauth-name-input:focus {
    border-color: oklch(0.72 0.26 310 / 0.55);
  }
  .btn.ghost {
    background: transparent;
    color: oklch(0.68 0.04 310 / 0.70);
    border: 1px solid transparent;
    font-size: 0.72rem;
    padding: 0.38rem 0.6rem;
  }
  .btn.ghost:hover:not(:disabled) {
    color: oklch(0.90 0.06 310);
    border-color: oklch(0.30 0.05 275 / 0.30);
  }
</style>
